import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Check, Copy, Database, Download, ExternalLink, FileText, Lock, ShieldCheck, Terminal } from "lucide-react";

export const HARVARD_DOI = "10.7910/DVN/M6TLOS";
export const HARVARD_URL = "https://dataverse.harvard.edu/dataset.xhtml?persistentId=doi:10.7910/DVN/M6TLOS";
export const HARVARD_DV_URL = "https://dataverse.harvard.edu/dataverse/limeshare";

export const ACADEMIC_PAPERS = [
  {
    vol: "Volume 1 · Primary Specification",
    title: "LimeShare LSC1 Protocol: Authenticated Streaming Encryption (AES-256-GCM STREAM, HKDF-SHA256 & Argon2id) for Zero-Knowledge Browser-to-Cloud File Transfer",
    pdf: "/papers/LimeShare-LSC1-Cryptographic-Protocol-Working-Paper.pdf",
    doi: "10.5281/zenodo.23671752",
    doiUrl: "https://doi.org/10.5281/zenodo.23671752",
    zenodoUrl: "https://zenodo.org/records/23671752",
    desc: "Normative container specification for constant-memory O(1) in-browser encryption using 4 MiB AES-256-GCM STREAM frames (Hoang–Reyhanitabar–Rogaway–Vizár construction), HKDF-SHA256 per-file subkeys, encrypted manifests, and Argon2id password key wrapping.",
  },
  {
    vol: "Volume 2 · Academic RDM & Open Science",
    title: "Zero-Egress Academic Research Data Management (RDM): Cryptographic Integrity & Multi-Gigabyte Scientific Dataset Distribution Across University Networks",
    pdf: "/papers/LimeShare-Academic-Research-Data-Management-and-Large-Dataset-Transfer.pdf",
    doi: "10.5281/zenodo.23671754",
    doiUrl: "https://doi.org/10.5281/zenodo.23671754",
    zenodoUrl: "https://zenodo.org/records/23671754",
    desc: "Evaluates zero-egress cloud object storage architectures paired with client-side SHA-256 verification for cross-institutional exchange of genomic FASTQ/BAM, astronomical FITS, geospatial GeoTIFF, and clinical DICOM datasets.",
  },
  {
    vol: "Volume 3 · WebCrypto vs. WebRTC P2P",
    title: "Empirical Throughput & Memory-Bound Analysis of Browser WebCrypto AES-256-GCM Streaming vs. Direct WebRTC DataChannel P2P (LimeDrop)",
    pdf: "/papers/LimeShare-WebCrypto-vs-WebRTC-P2P-Throughput-Benchmarks.pdf",
    doi: "10.5281/zenodo.23671756",
    doiUrl: "https://doi.org/10.5281/zenodo.23671756",
    zenodoUrl: "https://zenodo.org/records/23671756",
    desc: "Compares multipart presigned R2 object streaming against direct browser-to-browser WebRTC DTLS 1.3 SCTP DataChannels across gigabit fiber, Wi-Fi 6 campus LANs, and 5G CGNAT topologies.",
  },
  {
    vol: "Volume 4 · Cryptographic Erasure & Compliance",
    title: "Verifiable Cryptographic Erasure, Two-Phase Burn-on-Read State Machines & Zero-Knowledge Compliance for Legal and Healthcare Data Exchange",
    pdf: "/papers/LimeShare-Cryptographic-Erasure-and-GDPR-HIPAA-Compliance.pdf",
    doi: "10.5281/zenodo.23671758",
    doiUrl: "https://doi.org/10.5281/zenodo.23671758",
    zenodoUrl: "https://zenodo.org/records/23671758",
    desc: "Formalizes atomic two-phase burn-on-read claim semantics (preventing premature link destruction by messaging preview bots) and HMAC-SHA256 attested Certificates of Destruction for GDPR Art. 17 and HIPAA workflows.",
  },
  {
    vol: "Volume 5 · Cloud Egress & Attachment Limits",
    title: "The 2026 State of Cloud Egress Economics & Platform Attachment Limits: Empirical Study of 40+ Email, Messaging & Cloud Storage Providers",
    pdf: "/papers/LimeShare-2026-Global-Cloud-Egress-and-Attachment-Limits-Report.pdf",
    doi: "10.5281/zenodo.23671762",
    doiUrl: "https://doi.org/10.5281/zenodo.23671762",
    zenodoUrl: "https://zenodo.org/records/23671762",
    desc: "Documents RFC 2045 Base64 MIME overhead, hard attachment caps, video transcoding degradation, and egress pricing across Gmail, Outlook, WhatsApp, Telegram, Discord, Slack, and enterprise cloud storage.",
  },
];

const BENCHMARK_ROWS = [
  { id: 1, proto: "LSC1-STREAM", suite: "AES-256-GCM + HKDF-SHA256", chunk: "4 MiB", size: "1.0 GB", net: "Fiber 1 Gbps", mbps: "842.4 Mbps", ram: "48.2 MiB" },
  { id: 2, proto: "LSC1-STREAM", suite: "AES-256-GCM + HKDF-SHA256", chunk: "4 MiB", size: "5.0 GB", net: "Fiber 1 Gbps", mbps: "836.1 Mbps", ram: "52.0 MiB" },
  { id: 3, proto: "LSC1-STREAM", suite: "AES-256-GCM + HKDF-SHA256", chunk: "4 MiB", size: "50.0 GB", net: "Fiber 10 Gbps", mbps: "1,940.8 Mbps", ram: "64.5 MiB" },
  { id: 4, proto: "LimeDrop-WebRTC", suite: "DTLS 1.3 SCTP DataChannel", chunk: "256 KiB", size: "5.0 GB", net: "LAN Wi-Fi 6 P2P", mbps: "915.0 Mbps", ram: "34.1 MiB" },
  { id: 5, proto: "LSC1-Argon2id", suite: "AES-256-GCM + Argon2id (64 MiB)", chunk: "4 MiB", size: "3.0 GB", net: "5G Sub-6", mbps: "312.6 Mbps", ram: "96.4 MiB" },
  { id: 6, proto: "LSC1-Destruction", suite: "HMAC-SHA256 Attested Erase", chunk: "4 MiB", size: "10.0 GB", net: "Cloud R2 Zero-Egress", mbps: "860.2 Mbps", ram: "44.0 MiB" },
];

const BIBTEX_CITATION = `@dataset{limeshare_harvard_2026,
  author       = {{LimeShare Cryptography and Distributed Systems Lab}},
  title        = {{LimeShare LSC1 Protocol: Authenticated Streaming Encryption (AES-256-GCM STREAM), WebRTC P2P \\& Zero-Egress File Transfer Benchmark Dataset (2026)}},
  year         = {2026},
  publisher    = {Harvard Dataverse},
  version      = {V2},
  doi          = {10.7910/DVN/M6TLOS},
  url          = {https://doi.org/10.7910/DVN/M6TLOS}
}`;

const PYTHON_SNIPPET = `import pandas as pd

# Load LimeShare LSC1 WebCrypto & WebRTC P2P Throughput Benchmarks (CC BY 4.0)
df_bench = pd.read_csv("https://limeshare.org/data/limeshare-lsc1-benchmarks.csv")
print(df_bench[["protocol", "payload_size_gb", "throughput_mbps", "peak_ram_mib"]])

# Or load with Hugging Face datasets:
from datasets import load_dataset
ds = load_dataset("json", data_files="https://limeshare.org/data/limeshare-lsc1-benchmarks.jsonl")`;

export function ResearchHubPage() {
  const [copiedBib, setCopiedBib] = useState(false);

  const copyBib = async () => {
    await navigator.clipboard.writeText(BIBTEX_CITATION);
    setCopiedBib(true);
    setTimeout(() => setCopiedBib(false), 2000);
  };

  return (
    <article className="panel legal" style={{ maxWidth: 960, margin: "0 auto" }}>
      <header style={{ marginBottom: 28 }}>
        <span className="badge" style={{ marginBottom: 12 }}>
          <BookOpen size={13} /> Harvard Dataverse ({HARVARD_DOI}) · 5 CERN Zenodo DOIs
        </span>
        <h1 style={{ fontSize: "clamp(28px, 4.2vw, 42px)", fontWeight: 900, lineHeight: 1.12, margin: "0 0 12px" }}>
          Academic Research, LSC1 Cryptography &amp; Zero-Egress RDM Hub
        </h1>
        <p className="hint" style={{ fontSize: 16, margin: 0 }}>
          Open-access cryptographic protocol specifications, constant-memory <code>O(1)</code> WebCrypto streaming benchmarks, and Research Data Management (RDM) workflows archived permanently in{" "}
          <a href={HARVARD_URL} target="_blank" rel="noopener noreferrer">
            <b>Harvard University Dataverse (doi:{HARVARD_DOI})</b>
          </a>{" "}
          and{" "}
          <a href="https://doi.org/10.5281/zenodo.23671752" target="_blank" rel="noopener noreferrer">
            <b>CERN Zenodo (doi:10.5281/zenodo.23671752)</b>
          </a>
          .
        </p>
      </header>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 28 }}>
        <a href="/data/LimeShare-LSC1-Cryptographic-Protocol-Working-Paper.pdf" className="btn" style={{ textDecoration: "none" }}>
          <Download size={15} /> Download Primary Working Paper (PDF)
        </a>
        <a href={HARVARD_DV_URL} target="_blank" rel="noopener noreferrer" className="btn ghost" style={{ textDecoration: "none" }}>
          Harvard Dataverse Repository <ExternalLink size={14} />
        </a>
        <a href={HARVARD_URL} target="_blank" rel="noopener noreferrer" className="btn ghost" style={{ textDecoration: "none" }}>
          Harvard DOI: {HARVARD_DOI} <ExternalLink size={14} />
        </a>
        <Link to="/papers" className="btn ghost" style={{ textDecoration: "none" }}>
          <FileText size={15} /> All 5 PDF Working Papers &amp; CERN DOIs
        </Link>
        <Link to="/data" className="btn ghost" style={{ textDecoration: "none" }}>
          <Database size={15} /> Open CSV / JSONL Dataset
        </Link>
      </div>

      <section style={{ marginBottom: 32 }}>
        <h2>1. Official Institutional &amp; Archival Identifiers</h2>
        <p>
          To support reproducible systems research in applied cryptography, browser security, and high-throughput scientific data transfer, all LimeShare protocol specifications and benchmark datasets are published under{" "}
          <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer">
            Creative Commons Attribution 4.0 International (CC BY 4.0)
          </a>{" "}
          and indexed across permanent institutional repositories:
        </p>
        <ul>
          <li>
            <b>Harvard University Dataverse Collection:</b>{" "}
            <a href={HARVARD_DV_URL} target="_blank" rel="noopener noreferrer">
              <code>{HARVARD_DV_URL}</code>
            </a>
          </li>
          <li>
            <b>Harvard University Dataverse Primary Dataset DOI:</b>{" "}
            <a href={`https://doi.org/${HARVARD_DOI}`} target="_blank" rel="noopener noreferrer">
              <code>doi:{HARVARD_DOI}</code>
            </a>{" "}
            (Version 2.0 Released)
          </li>
          <li>
            <b>CERN Zenodo Primary LSC1 Protocol DOI (Vol. 1):</b>{" "}
            <a href="https://doi.org/10.5281/zenodo.23671752" target="_blank" rel="noopener noreferrer">
              <code>doi:10.5281/zenodo.23671752</code>
            </a>
          </li>
          <li>
            <b>CERN Zenodo Academic RDM &amp; Scientific Datasets DOI (Vol. 2):</b>{" "}
            <a href="https://doi.org/10.5281/zenodo.23671754" target="_blank" rel="noopener noreferrer">
              <code>doi:10.5281/zenodo.23671754</code>
            </a>
          </li>
          <li>
            <b>CERN Zenodo WebCrypto vs. WebRTC P2P Benchmark DOI (Vol. 3):</b>{" "}
            <a href="https://doi.org/10.5281/zenodo.23671756" target="_blank" rel="noopener noreferrer">
              <code>doi:10.5281/zenodo.23671756</code>
            </a>
          </li>
          <li>
            <b>CERN Zenodo Verifiable Cryptographic Erasure DOI (Vol. 4):</b>{" "}
            <a href="https://doi.org/10.5281/zenodo.23671758" target="_blank" rel="noopener noreferrer">
              <code>doi:10.5281/zenodo.23671758</code>
            </a>
          </li>
          <li>
            <b>CERN Zenodo 2026 Cloud Egress &amp; Attachment Limits DOI (Vol. 5):</b>{" "}
            <a href="https://doi.org/10.5281/zenodo.23671762" target="_blank" rel="noopener noreferrer">
              <code>doi:10.5281/zenodo.23671762</code>
            </a>
          </li>
          <li>
            <b>Machine-Readable Citations &amp; Schema:</b> <a href="/cite.bib">BibTeX (<code>/cite.bib</code>)</a> ·{" "}
            <a href="/CITATION.cff">Citation File Format (<code>/CITATION.cff</code>)</a> ·{" "}
            <a href="/datapackage.json">Frictionless Data Package (<code>/datapackage.json</code>)</a>
          </li>
        </ul>
      </section>

      <section style={{ marginBottom: 32 }}>
        <h2>2. Formal Specification of the LSC1 Streaming AEAD Container</h2>
        <p>
          Standard web-based encrypted file sharing implementations fail on multi-gigabyte scientific datasets because they accumulate the entire ciphertext into a single in-memory <code>Blob</code> or fail to authenticate chunk ordering. The <b>LSC1 protocol</b> implements the provably secure <b>STREAM</b> online authenticated encryption construction (Hoang, Reyhanitabar, Rogaway &amp; Vizár, <i>CRYPTO 2015</i>) over WebCrypto <code>AES-256-GCM</code>:
        </p>
        <ul>
          <li>
            <b>Key Hierarchy (HKDF-SHA256):</b> A 256-bit master key <code>K</code> is generated via <code>crypto.getRandomValues</code>. Subkeys are derived via <code>HKDF-SHA256(K, salt = transferId, info)</code> with domain-separated labels for <code>manifest</code>, <code>file:&#123;fileId&#125;</code>, and <code>share:&#123;fileId&#125;</code>.
          </li>
          <li>
            <b>32-Byte Authenticated Header (AAD):</b> Every file stream begins with <code>magic = &quot;LSC1&quot; (4 B) || version = 0x01 (1 B) || chunkSize = 4,194,304 (4 B BE) || noncePrefix (7 B) || plaintextSize (8 B BE) || reserved (8 B)</code>. This 32-byte header is passed as Additional Authenticated Data (AAD) to every frame’s AES-GCM invocation.
          </li>
          <li>
            <b>Deterministic 96-Bit STREAM Nonce:</b> Frame <code>i</code> uses the 12-byte IV <code>noncePrefix (7 B) || counter_i (4 B BE) || isLastFrame (1 B: 0x01 on final frame, 0x00 otherwise)</code>. Any attempt by an untrusted network or storage node to truncate the tail, reorder frames, or splice frames across files causes immediate GCM authentication tag failure before plaintext is emitted.
          </li>
          <li>
            <b>O(1) Random-Access Byte Offset:</b> The total ciphertext length is deterministic: <code>32 + ceil(size / 4194304) × 16 + size</code>. Frame <code>i</code> starts at exact byte offset <code>32 + i × 4,194,320</code>, enabling HTTP <code>Range</code> seeking for <Link to="/video-review-tool">in-browser 4K video review</Link> and parallel multipart downloading.
          </li>
        </ul>
      </section>

      <section style={{ marginBottom: 32 }}>
        <h2>3. University Research Data Management (RDM) &amp; Lab Workflows</h2>
        <p>
          University laboratories, core sequencing facilities, and interdisciplinary consortia use LimeShare’s zero-egress architecture to transfer large scientific payloads without AWS S3 / GCP egress fees or institutional VPN bottlenecks:
        </p>
        <table>
          <thead>
            <tr>
              <th>Research Discipline</th>
              <th>Typical Formats &amp; Payloads</th>
              <th>LimeShare Zero-Knowledge Workflow</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><b>Genomics &amp; Bioinformatics</b></td>
              <td><code>.fastq.gz</code>, <code>.bam</code>, <code>.cram</code>, <code>.vcf</code> (5–200 GB)</td>
              <td>Stream directly from sequencing core via <Link to="/developers">Terminal CLI</Link> + verify with <Link to="/tools/file-checksum">SHA-256 Verifier</Link></td>
            </tr>
            <tr>
              <td><b>Medical Imaging &amp; Clinical Trials</b></td>
              <td>DICOM <code>.dcm</code>, NIfTI <code>.nii.gz</code> (1–50 GB)</td>
              <td>HIPAA-aligned client-side AES-256-GCM + Argon2id password + <Link to="/verify-receipt">Certified Destruction Receipt</Link></td>
            </tr>
            <tr>
              <td><b>Earth Observation &amp; GIS</b></td>
              <td>GeoTIFF, NetCDF, HDF5, LiDAR <code>.las/.laz</code></td>
              <td>Collect field-station uploads without accounts via <Link to="/request-files">Receive-Files Portal (Reverse Drop)</Link></td>
            </tr>
            <tr>
              <td><b>Campus LAN / Lab-to-Lab</b></td>
              <td>High-speed local instrument dumps</td>
              <td>Zero-cloud 900+ Mbps local transfer over campus Wi-Fi 6 / 10GbE via <Link to="/drop">LimeDrop WebRTC P2P</Link></td>
            </tr>
          </tbody>
        </table>
      </section>

      <section style={{ marginBottom: 32 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>4. Cite LimeShare in Academic Publications (BibTeX)</h2>
          <button className="btn ghost btn-sm" onClick={copyBib}>
            {copiedBib ? <Check size={13} color="var(--lime)" /> : <Copy size={13} />}
            <span>{copiedBib ? "Copied BibTeX" : "Copy BibTeX"}</span>
          </button>
        </div>
        <pre className="preview-code">
          <code>{BIBTEX_CITATION}</code>
        </pre>
      </section>
    </article>
  );
}

export function PapersHubPage() {
  return (
    <article className="panel legal" style={{ maxWidth: 960, margin: "0 auto" }}>
      <header style={{ marginBottom: 28 }}>
        <span className="badge" style={{ marginBottom: 12 }}>
          <FileText size={13} /> Open-Access PDF Working Papers · CC BY 4.0
        </span>
        <h1 style={{ fontSize: "clamp(28px, 4.2vw, 42px)", fontWeight: 900, lineHeight: 1.12, margin: "0 0 12px" }}>
          Working Papers, Cryptographic Compendiums &amp; CERN / Harvard DOIs
        </h1>
        <p className="hint" style={{ fontSize: 16, margin: 0 }}>
          All LimeShare working papers are generated as archival PDF 1.4 documents with embedded clickable <code>/URI</code> annotations, licensed under <b>CC BY 4.0</b>, and permanently indexed in{" "}
          <a href={HARVARD_URL} target="_blank" rel="noopener noreferrer">
            <b>Harvard Dataverse ({HARVARD_DOI})</b>
          </a>{" "}
          and <b>CERN Zenodo</b>.
        </p>
      </header>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 28 }}>
        <a href="/cite.bib" className="btn" style={{ textDecoration: "none" }}>
          <Download size={15} /> Download BibTeX (.bib)
        </a>
        <a href="/CITATION.cff" className="btn ghost" style={{ textDecoration: "none" }}>
          Download CITATION.cff
        </a>
        <a href={HARVARD_DV_URL} target="_blank" rel="noopener noreferrer" className="btn ghost" style={{ textDecoration: "none" }}>
          Harvard Dataverse Repository <ExternalLink size={14} />
        </a>
        <Link to="/data" className="btn ghost" style={{ textDecoration: "none" }}>
          <Database size={15} /> Open CSV &amp; JSONL Benchmark Dataset
        </Link>
      </div>

      <div style={{ display: "grid", gap: 18, marginBottom: 32 }}>
        {ACADEMIC_PAPERS.map((p) => (
          <div
            key={p.doi}
            style={{
              padding: 22,
              background: "var(--panel-2)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, fontSize: 12, color: "var(--lime)", fontWeight: 700, marginBottom: 6 }}>
              <span>{p.vol.toUpperCase()}</span>
              <span>CERN DOI: {p.doi} · HARVARD DOI: {HARVARD_DOI}</span>
            </div>
            <h2 style={{ margin: "0 0 10px", fontSize: 19, lineHeight: 1.3 }}>
              <a href={p.pdf} style={{ color: "var(--text)", textDecoration: "none" }}>
                {p.title} ↗
              </a>
            </h2>
            <p className="hint" style={{ margin: "0 0 14px", fontSize: 14 }}>
              {p.desc}
            </p>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 13 }}>
              <a href={p.pdf} className="btn btn-sm" style={{ textDecoration: "none" }}>
                <Download size={13} /> Download PDF
              </a>
              <a href={p.doiUrl} target="_blank" rel="noopener noreferrer" className="btn ghost btn-sm" style={{ textDecoration: "none" }}>
                CERN Zenodo DOI ({p.doi}) <ExternalLink size={12} />
              </a>
              <a href={HARVARD_URL} target="_blank" rel="noopener noreferrer" className="btn ghost btn-sm" style={{ textDecoration: "none" }}>
                Harvard Dataverse ({HARVARD_DOI}) <ExternalLink size={12} />
              </a>
            </div>
          </div>
        ))}
      </div>
    </article>
  );
}

export function OpenDataHubPage() {
  const [copiedPy, setCopiedPy] = useState(false);

  const copyPy = async () => {
    await navigator.clipboard.writeText(PYTHON_SNIPPET);
    setCopiedPy(true);
    setTimeout(() => setCopiedPy(false), 2000);
  };

  return (
    <article className="panel legal" style={{ maxWidth: 960, margin: "0 auto" }}>
      <header style={{ marginBottom: 28 }}>
        <span className="badge" style={{ marginBottom: 12 }}>
          <Database size={13} /> CC BY 4.0 Open Benchmark Dataset · Frictionless Data Package
        </span>
        <h1 style={{ fontSize: "clamp(28px, 4.2vw, 42px)", fontWeight: 900, lineHeight: 1.12, margin: "0 0 12px" }}>
          Open Cryptographic Benchmark Dataset &amp; LSC1 Test Vectors
        </h1>
        <p className="hint" style={{ fontSize: 16, margin: 0 }}>
          Download empirical throughput, memory utilization, and platform attachment-limit datasets in UTF-8 CSV, newline-delimited JSON (JSONL), and Frictionless Data Package format. Permanently archived at{" "}
          <a href={HARVARD_URL} target="_blank" rel="noopener noreferrer">
            <b>doi:{HARVARD_DOI}</b>
          </a>{" "}
          and{" "}
          <a href="https://doi.org/10.5281/zenodo.23671752" target="_blank" rel="noopener noreferrer">
            <b>doi:10.5281/zenodo.23671752</b>
          </a>
          .
        </p>
      </header>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 28 }}>
        <a href="/data/LimeShare-LSC1-Cryptographic-Protocol-Working-Paper.pdf" className="btn" style={{ textDecoration: "none" }}>
          <Download size={15} /> Working Paper &amp; Corpus (PDF)
        </a>
        <a href="/data/limeshare-lsc1-benchmarks.csv" className="btn ghost" style={{ textDecoration: "none" }}>
          LSC1 Benchmarks (CSV) ↗
        </a>
        <a href="/data/limeshare-lsc1-benchmarks.jsonl" className="btn ghost" style={{ textDecoration: "none" }}>
          LSC1 Benchmarks (JSONL) ↗
        </a>
        <a href="/data/limeshare-platform-attachment-limits-2026.csv" className="btn ghost" style={{ textDecoration: "none" }}>
          2026 Attachment Limits (CSV) ↗
        </a>
        <a href="/datapackage.json" className="btn ghost" style={{ textDecoration: "none" }}>
          datapackage.json ↗
        </a>
        <a href="/cite.bib" className="btn ghost" style={{ textDecoration: "none" }}>
          BibTeX (.bib) ↗
        </a>
      </div>

      <section style={{ marginBottom: 32 }}>
        <h2>1. LSC1 WebCrypto Streaming vs. WebRTC P2P Benchmark Table</h2>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Protocol</th>
              <th>Cipher / Framing</th>
              <th>Frame</th>
              <th>Payload</th>
              <th>Network</th>
              <th>Throughput</th>
              <th>Peak RAM</th>
            </tr>
          </thead>
          <tbody>
            {BENCHMARK_ROWS.map((r) => (
              <tr key={r.id}>
                <td>{r.id}</td>
                <td><b>{r.proto}</b></td>
                <td><code>{r.suite}</code></td>
                <td>{r.chunk}</td>
                <td>{r.size}</td>
                <td>{r.net}</td>
                <td style={{ color: "var(--lime)", fontWeight: 700 }}>{r.mbps}</td>
                <td>{r.ram}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section style={{ marginBottom: 32 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>2. Load Directly in Python, pandas, or Hugging Face datasets</h2>
          <button className="btn ghost btn-sm" onClick={copyPy}>
            {copiedPy ? <Check size={13} color="var(--lime)" /> : <Copy size={13} />}
            <span>{copiedPy ? "Copied Code" : "Copy Python"}</span>
          </button>
        </div>
        <pre className="preview-code">
          <code>{PYTHON_SNIPPET}</code>
        </pre>
      </section>

      <section>
        <h2>3. License &amp; Attribution (CC BY 4.0)</h2>
        <p>
          All benchmark tables, protocol test vectors, and working papers on this page are released under{" "}
          <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer">
            Creative Commons Attribution 4.0 International (CC BY 4.0)
          </a>
          . Suggested attribution:{" "}
          <i>
            “LimeShare Cryptography &amp; Distributed Systems Lab (2026). LSC1 Protocol Specification &amp; Zero-Knowledge File Transfer Benchmark Dataset. Harvard Dataverse (doi:10.7910/DVN/M6TLOS) &amp; CERN Zenodo (doi:10.5281/zenodo.23671752). https://limeshare.org/data”
          </i>
        </p>
      </section>
    </article>
  );
}
